import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Swal from 'sweetalert2';
import axios from 'axios';
import moment from 'moment';
import { useTranslation } from 'react-i18next';
import StartReservationModal from './StartReservationModal';
import { Eye, Edit, Search, RefreshCw, Filter, X, Play } from 'lucide-react';

const ReservationList = ({ defaultStatus = '' }) => {
    const { t, i18n } = useTranslation();
    const isRtl = i18n.language === 'ar';
    const [reservations, setReservations] = useState([]);
    const [clients, setClients] = useState([]);
    const [voitures, setVoitures] = useState([]);
    const [clientDetails, setClientDetails] = useState({});
    const [voitureDetails, setVoitureDetails] = useState({});
    const navigate = useNavigate();

    // Filter state
    const [status, setStatus] = useState(defaultStatus);
    const [clientId, setClientId] = useState('');
    const [voitureId, setVoitureId] = useState('');
    const [dateFrom, setDateFrom] = useState('');
    const [dateTo, setDateTo] = useState('');
    const [loading, setLoading] = useState(false);
    const [error, setError] = useState('');
    const [showFilters, setShowFilters] = useState(false);
    const [startingReservation, setStartingReservation] = useState(null);

    // Fetch filters and data
    useEffect(() => {
        fetchClients();
        fetchVoitures();
        fetchReservations();
    }, []);

    const fetchClients = async () => {
        try {
            const res = await axios.get('https://krili-loto.com/api/clients');
            setClients(res.data);

            // Create a clients map for easy lookup
            const clientsMap = {};
            res.data.forEach(client => {
                clientsMap[client.id] = client;
            });
            setClientDetails(clientsMap);
        } catch (err) {
            setError(t('failed_load_clients'));
        }
    };

    const fetchVoitures = async () => {
        try {
            const res = await axios.get('https://krili-loto.com/api/voitures');
            setVoitures(res.data);

            // Create a voitures map for easy lookup
            const voituresMap = {};
            res.data.forEach(voiture => {
                voituresMap[voiture.id] = voiture;
            });
            setVoitureDetails(voituresMap);
        } catch (err) {
            setError(t('failed_load_vehicles'));
        }
    };

    const fetchReservations = async () => {
        setLoading(true);
        setError('');
        try {
            const params = {};
            if (status) params.status = status;
            if (clientId) params.client_id = clientId;
            if (voitureId) params.voiture_id = voitureId;
            if (dateFrom) params.date_from = dateFrom;
            if (dateTo) params.date_to = dateTo;

            const res = await axios.get('https://krili-loto.com/api/reservations/status/en_attente', { params });

            // Handle potentially paginated response
            const reservationsData = res.data.data || res.data;
            setReservations(reservationsData);

            // Fetch additional details for each reservation
            await fetchReservationDetails(reservationsData);
        } catch (err) {
            setError(t('failed_load_reservations'));
        } finally {
            setLoading(false);
        }
    };

    const fetchReservationDetails = async (reservations) => {
        try {
            // Fetch client details for each unique client ID
            const uniqueClientIds = [...new Set(reservations.map(res => res.client_id))];
            const clientPromises = uniqueClientIds.map(clientId =>
                axios.get(`https://krili-loto.com/api/clients/${clientId}`)
            );

            const clientResponses = await Promise.all(clientPromises);
            const updatedClientDetails = { ...clientDetails };
            clientResponses.forEach(response => {
                updatedClientDetails[response.data.id] = response.data;
            });
            setClientDetails(updatedClientDetails);

            // Fetch vehicle details for each unique vehicle ID
            const uniqueVoitureIds = [...new Set(reservations.map(res => res.voiture_id))];
            const voiturePromises = uniqueVoitureIds.map(voitureId =>
                axios.get(`https://krili-loto.com/api/voitures/${voitureId}`)
            );

            const voitureResponses = await Promise.all(voiturePromises);
            const updatedVoitureDetails = { ...voitureDetails };
            voitureResponses.forEach(response => {
                updatedVoitureDetails[response.data.id] = response.data;
            });
            setVoitureDetails(updatedVoitureDetails);

        } catch (err) {
            console.error('Error fetching reservation details:', err);
        }
    };

    const getClientName = (clientId) => {
        const client = clientDetails[clientId];
        if (client) {
            return `${client.prenom} ${client.nom}`;
        }
        return `${t('client')} #${clientId}`;
    };

    const getVoitureInfo = (voitureId) => {
        const voiture = voitureDetails[voitureId];
        if (voiture) {
            return `${voiture.marque} ${voiture.modele} `;
        }
        return `${t('vehicle')} #${voitureId}`;
    };

    const handleDeleteReservation = async (id) => {
        Swal.fire({
            title: t('are_you_sure'),
            text: t('confirm_delete_reservation'),
            icon: 'warning',
            showCancelButton: true,
            confirmButtonText: t('yes_delete'),
            cancelButtonText: t('cancel'),
            reverseButtons: true
        }).then(async (result) => {
            if (result.isConfirmed) {
                try {
                    await axios.delete(`https://krili-loto.com/api/reservations/${id}`);
                    fetchReservations();
                    Swal.fire(
                        t('deleted'),
                        t('success_delete_reservation'),
                        'success'
                    );
                } catch (err) {
                    console.error(t('error_deleting_reservation'), err);
                    setError(t('error_delete_reservation'));
                    Swal.fire(
                        t('error'),
                        t('error_delete_reservation'),
                        'error'
                    );
                }
            }
        });
    };

    const handleReservationStarted = () => {
        setStartingReservation(null);
        fetchReservations();
    };

    const formatDate = (dateString) => {
        if (!dateString) return '-';
        return moment(dateString).format('DD/MM/YYYY HH:mm');
    };

    const formatCurrency = (amount) => {
        if (amount === null || amount === undefined) return '-';
        return new Intl.NumberFormat(i18n.language === 'ar' ? 'ar-SA' : 'fr-FR', {
            style: 'currency',
            currency: 'DZD'
        }).format(amount).replace('DZD', 'DA');
    };

    const getStatusBadge = (status) => {
        const statusClasses = {
            'en_attente': 'bg-amber-100 text-amber-800',
            'en_cours': 'bg-blue-100 text-blue-800',
            'done': 'bg-green-100 text-green-800',
            'canceled': 'bg-red-100 text-red-800',
            'en_retard': 'bg-orange-100 text-orange-800'
        };

        const statusLabels = {
            'en_attente': t('pending'),
            'en_cours': t('en_cours'),
            'done': t('completed'),
            'canceled': t('cancelled'),
            'en_retard': t('en_retard')
        };

        return (
            <span className={`px-3 py-1 rounded-full text-xs font-medium ${statusClasses[status]}`}>
                {statusLabels[status]}
            </span>
        );
    };

    const handleViewDetails = (id) => {
        navigate(`/reservations/${id}`);
    };

    const handleEditReservation = (id) => {
        navigate(`/reservations/${id}/edit`);
    };

    const clearFilters = () => {
        setStatus('');
        setClientId('');
        setVoitureId('');
        setDateFrom('');
        setDateTo('');
    };

    return (
        <div className="p-6 bg-gray-50 min-h-screen" dir={isRtl ? 'rtl' : 'ltr'}>
            <div className="max-w-7xl mx-auto">
                {/* Header */}
                <div className={`flex justify-between items-center mb-6 ${isRtl ? 'flex-row-reverse' : ''}`}>
                    <h1 className={`text-2xl font-bold text-gray-800 ${isRtl ? 'text-right' : 'text-left'}`}>{t('reservations_management')}</h1>
                    <div className={`flex ${isRtl ? 'flex-row-reverse space-x-reverse' : 'space-x-2'}`}>
                        <button
                            onClick={() => setShowFilters(!showFilters)}
                            className={`flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 ${isRtl ? 'flex-row-reverse' : ''}`}
                        >
                            <Filter className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                            {t('filters')}
                        </button>
                        <button
                            onClick={fetchReservations}
                            className={`flex items-center px-4 py-2 bg-white border border-gray-300 rounded-lg shadow-sm text-sm font-medium text-gray-700 hover:bg-gray-50 ${isRtl ? 'flex-row-reverse' : ''}`}
                        >
                            <RefreshCw className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'} ${loading ? 'animate-spin' : ''}`} />
                            {t('refresh')}
                        </button>
                    </div>
                </div>

                {/* Filter Panel */}
                {showFilters && (
                    <div className={`bg-white p-4 rounded-lg shadow-md mb-6 border border-gray-200 ${isRtl ? 'text-right' : 'text-left'}`}>
                        <div className={`flex justify-between items-center mb-4 ${isRtl ? 'flex-row-reverse' : ''}`}>
                            <h2 className="text-lg font-medium text-gray-800">{t('filter_reservations')}</h2>
                            <button
                                onClick={clearFilters}
                                className={`text-sm text-blue-600 hover:text-blue-800 flex items-center ${isRtl ? 'flex-row-reverse' : ''}`}
                            >
                                <X className={`w-4 h-4 ${isRtl ? 'ml-1' : 'mr-1'}`} /> {t('clear_all')}
                            </button>
                        </div>

                        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-5 gap-4">
                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('status')}</label>
                                <select
                                    value={status}
                                    onChange={(e) => setStatus(e.target.value)}
                                    className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                                >
                                    <option value="">{t('all_statuses')}</option>
                                    <option value="en_attente">{t('pending')}</option>
                                    <option value="en_cours">{t('en_cours')}</option>
                                    <option value="done">{t('completed')}</option>
                                    <option value="canceled">{t('cancelled')}</option>
                                    <option value="en_retard">{t('en_retard')}</option>
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('client')}</label>
                                <select
                                    value={clientId}
                                    onChange={(e) => setClientId(e.target.value)}
                                    className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                                >
                                    <option value="">{t('all_clients')}</option>
                                    {clients.map((client) => (
                                        <option key={client.id} value={client.id}>
                                            {client.prenom} {client.nom}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('vehicle')}</label>
                                <select
                                    value={voitureId}
                                    onChange={(e) => setVoitureId(e.target.value)}
                                    className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                                >
                                    <option value="">{t('all_vehicles')}</option>
                                    {voitures.map((voiture) => (
                                        <option key={voiture.id} value={voiture.id}>
                                            {voiture.marque} {voiture.modele}
                                        </option>
                                    ))}
                                </select>
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('departure_date')} ({t('from')})</label>
                                <input
                                    type="date"
                                    value={dateFrom}
                                    onChange={(e) => setDateFrom(e.target.value)}
                                    className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                                />
                            </div>

                            <div>
                                <label className="block text-sm font-medium text-gray-700 mb-1">{t('departure_date')} ({t('to')})</label>
                                <input
                                    type="date"
                                    value={dateTo}
                                    onChange={(e) => setDateTo(e.target.value)}
                                    className={`w-full p-2 border border-gray-300 rounded-md shadow-sm focus:ring-blue-500 focus:border-blue-500 ${isRtl ? 'text-right' : 'text-left'}`}
                                />
                            </div>
                        </div>

                        <div className={`mt-4 flex ${isRtl ? 'justify-start' : 'justify-end'}`}>
                            <button
                                onClick={fetchReservations}
                                className={`flex items-center px-4 py-2 bg-blue-600 text-white rounded-md shadow-sm hover:bg-blue-700 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-blue-500 ${isRtl ? 'flex-row-reverse' : ''}`}
                                disabled={loading}
                            >
                                <Search className={`w-4 h-4 ${isRtl ? 'ml-2' : 'mr-2'}`} />
                                {loading ? t('searching') : t('search')}
                            </button>
                        </div>
                    </div>
                )}

                {/* Results */}
                <div className={`bg-white shadow overflow-hidden sm:rounded-lg ${isRtl ? 'text-right' : 'text-left'}`}>
                    <div className="px-4 py-5 sm:px-6 border-b border-gray-200">
                        <h3 className="text-lg leading-6 font-medium text-gray-900">
                            {t('results')}
                        </h3>
                        <p className="mt-1 text-sm text-gray-500">
                            {t('reservations_results_subtitle')}
                        </p>
                    </div>

                    {loading ? (
                        <div className="flex justify-center items-center p-8">
                            <div className="animate-spin rounded-full h-12 w-12 border-t-2 border-b-2 border-blue-500"></div>
                        </div>
                    ) : error ? (
                        <div className={`p-4 bg-red-50 border-l-4 border-red-400 ${isRtl ? 'border-l-0 border-r-4' : ''}`}>
                            <div className={`flex ${isRtl ? 'flex-row-reverse' : ''}`}>
                                <div className="flex-shrink-0">
                                    <svg className="h-5 w-5 text-red-400" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor">
                                        <path fillRule="evenodd" d="M10 18a8 8 0 100-16 8 8 0 000 16zM8.707 7.293a1 1 0 00-1.414 1.414L8.586 10l-1.293 1.293a1 1 0 101.414 1.414L10 11.414l1.293 1.293a1 1 0 001.414-1.414L11.414 10l1.293-1.293a1 1 0 00-1.414-1.414L10 8.586 8.707 7.293z" clipRule="evenodd" />
                                    </svg>
                                </div>
                                <div className={`${isRtl ? 'mr-3' : 'ml-3'}`}>
                                    <p className={`text-sm text-red-700 ${isRtl ? 'text-right' : 'text-left'}`}>
                                        {error}
                                    </p>
                                </div>
                            </div>
                        </div>
                    ) : reservations.length === 0 ? (
                        <div className="p-8 text-center">
                            <svg className="mx-auto h-12 w-12 text-gray-400" fill="none" viewBox="0 0 24 24" stroke="currentColor">
                                <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M9.172 16.172a4 4 0 015.656 0M9 10h.01M15 10h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                            </svg>
                            <h3 className="mt-2 text-sm font-medium text-gray-900">{t('no_reservations_found')}</h3>
                            <p className="mt-1 text-sm text-gray-500">
                                {t('try_modifying_filters')}
                            </p>
                        </div>
                    ) : (
                        <>
                            <div className="hidden md:block overflow-x-auto">
                                <table className="min-w-full divide-y divide-gray-200" dir={isRtl ? 'rtl' : 'ltr'}>
                                    <thead className="bg-gray-50">
                                        <tr>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('id')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('client')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('vehicle')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('dates')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('pricing')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-right' : 'text-left'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('status')}
                                            </th>
                                            <th scope="col" className={`px-6 py-3 ${isRtl ? 'text-left' : 'text-right'} text-xs font-medium text-gray-500 uppercase tracking-wider`}>
                                                {t('actions')}
                                            </th>
                                        </tr>
                                    </thead>
                                    <tbody className="bg-white divide-y divide-gray-200">
                                        {reservations.map((res) => (
                                            <tr key={res.id} className="hover:bg-gray-50">
                                                <td className={`px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    #{res.id}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    <div className="text-sm font-medium text-gray-900">{getClientName(res.client_id)}</div>
                                                    <div className="text-sm text-gray-500">{t('id')}: {res.client_id}</div>
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    <div className="text-sm font-medium text-gray-900">{getVoitureInfo(res.voiture_id)}</div>
                                                    <div className="text-sm text-gray-500">
                                                        {res.lieu_depart} → {res.lieu_retour}
                                                    </div>
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    <div className="text-sm text-gray-900"><span className="font-medium">{t('departure')}:</span> {formatDate(res.date_depart)}</div>
                                                    <div className="text-sm text-gray-500"><span className="font-medium">{t('return')}:</span> {formatDate(res.date_retour_prevue)}</div>
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    <div className="text-sm text-gray-900"><span className="font-medium">{t('total')}:</span> {formatCurrency(res.total)}</div>
                                                    <div className="text-sm text-gray-500"><span className="font-medium">{t('paid')}:</span> {formatCurrency(res.total_paye)}</div>
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-right' : 'text-left'}`}>
                                                    {getStatusBadge(res.status)}
                                                </td>
                                                <td className={`px-6 py-4 whitespace-nowrap ${isRtl ? 'text-left' : 'text-right'} text-sm font-medium`}>
                                                    <div className={`flex justify-end ${isRtl ? 'space-x-reverse space-x-2' : 'space-x-2'}`}>
                                                        <button
                                                            onClick={() => handleDeleteReservation(res.id)}
                                                            className="text-red-600 hover:text-red-900 p-1 rounded-full hover:bg-red-50"
                                                            title={t('delete')}
                                                        >
                                                            <X className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleViewDetails(res.id)}
                                                            className="text-blue-600 hover:text-blue-900 p-1 rounded-full hover:bg-blue-50"
                                                            title={t('view_details')}
                                                        >
                                                            <Eye className="w-5 h-5" />
                                                        </button>
                                                        <button
                                                            onClick={() => handleEditReservation(res.id)}
                                                            className="text-green-600 hover:text-green-900 p-1 rounded-full hover:bg-green-50"
                                                            title={t('edit_reservation')}
                                                        >
                                                            <Edit className="w-5 h-5" />
                                                        </button>
                                                        {res.status === 'en_attente' && (
                                                            <button
                                                                onClick={() => setStartingReservation(res)}
                                                                className="text-purple-600 hover:text-purple-900 p-1 rounded-full hover:bg-purple-50"
                                                                title={t('start_reservation')}
                                                            >
                                                                <Play className="w-5 h-5" />
                                                            </button>
                                                        )}
                                                    </div>
                                                </td>
                                            </tr>
                                        ))}
                                    </tbody>
                                </table>
                            </div>
                            <div className="block md:hidden space-y-4 p-4">
                                {reservations.map((res) => (
                                    <div key={res.id} className="bg-white rounded-xl shadow-sm border border-gray-200 p-4 space-y-3">
                                        <div className="flex justify-between items-start">
                                            <span className="text-sm font-semibold text-gray-900">#{res.id}</span>
                                            {getStatusBadge(res.status)}
                                        </div>
                                        <div className="space-y-1">
                                            <p className="text-sm font-medium text-gray-900"><span className="text-gray-500 font-normal">{t('client')}:</span> {getClientName(res.client_id)} (ID: {res.client_id})</p>
                                            <p className="text-sm text-gray-700"><span className="text-gray-500">{t('vehicle')}:</span> {getVoitureInfo(res.voiture_id)}</p>
                                            <p className="text-xs text-gray-500">{res.lieu_depart} → {res.lieu_retour}</p>
                                        </div>
                                        <div className="border-t border-gray-100 pt-2 space-y-1">
                                            <p className="text-xs text-gray-600"><strong>{t('departure')}:</strong> {formatDate(res.date_depart)}</p>
                                            <p className="text-xs text-gray-600"><strong>{t('return')}:</strong> {formatDate(res.date_retour_prevue)}</p>
                                        </div>
                                        <div className="border-t border-gray-100 pt-2 flex justify-between items-center">
                                            <div>
                                                <p className="text-xs text-gray-500">{t('total')}: <span className="font-semibold text-gray-900">{formatCurrency(res.total)}</span></p>
                                                <p className="text-xs text-gray-500">{t('paid')}: <span className="font-semibold text-green-600">{formatCurrency(res.total_paye)}</span></p>
                                            </div>
                                            <div className="flex space-x-1">
                                                <button
                                                    onClick={() => handleDeleteReservation(res.id)}
                                                    className="text-red-600 p-2 rounded-lg hover:bg-red-50"
                                                >
                                                    <X className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleViewDetails(res.id)}
                                                    className="text-blue-600 p-2 rounded-lg hover:bg-blue-50"
                                                >
                                                    <Eye className="w-5 h-5" />
                                                </button>
                                                <button
                                                    onClick={() => handleEditReservation(res.id)}
                                                    className="text-green-600 p-2 rounded-lg hover:bg-green-50"
                                                >
                                                    <Edit className="w-5 h-5" />
                                                </button>
                                                {res.status === 'en_attente' && (
                                                    <button
                                                        onClick={() => setStartingReservation(res)}
                                                        className="text-purple-600 p-2 rounded-lg hover:bg-purple-50"
                                                    >
                                                        <Play className="w-5 h-5" />
                                                    </button>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                ))}
                            </div>
                        </>
                    )}
                </div>

                {/* Add the modal outside the table */}
                {startingReservation && (
                    <StartReservationModal
                        reservation={startingReservation}
                        onClose={() => setStartingReservation(null)}
                        onSuccess={handleReservationStarted}
                    />
                )}
            </div>
        </div>
    );
};

export default ReservationList;
